import React, { useState, useCallback, useEffect } from 'react';
import Button from '@msi/cobalt-react/button';
import Icon from '@msi/cobalt-react/icon';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate } from 'react-router-dom';
import { formatDateForDisplay } from '../../helpers/DateHelper';
import style from './ApiDetails.module.css';
import { Api, ApiAttachment, ApimGatewayUrls } from '../../types/apiData';
import LifecycleBadge from '../LifecycleBadge/LifecycleBadge';
import { ApimProductBadge } from '../ApimProductBadge/ApimProductBadge';
import HealthScoreBadge from '../HealthScoreBadge/HealthScoreBadge';
import ApiService from '../../services/apiService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const GATEWAY_ENVIRONMENTS: Array<keyof ApimGatewayUrls> = ['dev', 'stage', 'prod'];

interface ApiDetailsProps {
    api: Api;
}

const ApiDetails: React.FC<ApiDetailsProps> = ({ api }) => {
    const { authState } = useOktaAuth();
    const navigate = useNavigate();
    const [apiService] = useState(() => new ApiService());
    const [isBasicInformationHidden, setIsBasicInformationHidden] = useState(false);
    const [isDataHidden, setIsDataHidden] = useState(false);
    const [isContactHidden, setIsContactHidden] = useState(false);
    const [isOtherHidden, setIsOtherHidden] = useState(false);
    const [isGovernanceMetadataHidden, setIsGovernanceMetadataHidden] = useState(false);
    const [fetchedGatewayUrls, setFetchedGatewayUrls] = useState<ApimGatewayUrls | undefined>(undefined);
    const [isLoadingGatewayUrls, setIsLoadingGatewayUrls] = useState(false);
    const [gatewayUrlsError, setGatewayUrlsError] = useState<string | null>(null);

    const gatewayUrls = fetchedGatewayUrls ?? api.apiCenterMetadata?.apimGatewayUrls;
    const isApimPublished = api.developerProgram && (api.apiCenterMetadata?.apimProducts?.length ?? 0) > 0;

    // Gateway URLs rarely change, so this only auto-fetches once, and only when none are stored yet.
    useEffect(() => {
        setFetchedGatewayUrls(undefined);
        setGatewayUrlsError(null);
        if (!isApimPublished) {
            return;
        }

        const existingGatewayUrls = api.apiCenterMetadata?.apimGatewayUrls;
         if (existingGatewayUrls && GATEWAY_ENVIRONMENTS.some((env) => existingGatewayUrls[env])) {
             return;
         }

        let cancelled = false;
        (async () => {
            setIsLoadingGatewayUrls(true);
            try {
                const updatedApi = await apiService.refreshApimGatewayUrls(api.id, authState);
                if (!cancelled) {
                    setFetchedGatewayUrls(updatedApi.apiCenterMetadata?.apimGatewayUrls);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Error fetching APIM gateway URLs:', error);
                    setGatewayUrlsError('Failed to load APIM endpoint URLs.');
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingGatewayUrls(false);
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api.id, isApimPublished]);

    // Check if API has an API Specification object stored in GCP
    // Legacy string URLs (e.g., GitLab links) will evaluate to false
    const hasApiSpec = Boolean(
        api.apiSpecification &&
        typeof api.apiSpecification === 'object' &&
        api.apiSpecification.url
    );

    const downloadFile = useCallback(async (fileUrl: string, fileName: string) => {
        try {
            const token = authState?.accessToken?.accessToken;
            if (!token) {
                console.error('Authentication token not available. Please sign in again.');
                return;
            }
            const response = await fetch(
                `${API_BASE_URL}/attachments_download?fileUrl=${encodeURIComponent(fileUrl)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) {
                throw new Error(`Failed to download file: ${response.statusText}`);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    }, [authState]);

    const Field = ({ label, value, isUrl = false }: { label: React.ReactNode, value: any, isUrl?: boolean }) => (
        <div className={`mb-3 ${style.field}`}>
            <label className={style.formLabel}>{label}</label>
            <div className={style.formControlPlaintext}>
                {isUrl
                    ? (value ? <a href={value} target="_blank" rel="noopener noreferrer">{value}</a> : <span className="text-muted">-</span>)
                    : (value || <span className="text-muted">-</span>)
                }
            </div>
        </div>
    );

    const HideExpandButton = ({ isHidden, toggle }: { isHidden: boolean, toggle: () => void }) => (
        <div className="dropdown-icon-container">
            <Button className={style.toggleButton} onClick={toggle}>
                <Icon name={isHidden ? "ic_chevron_down" : "ic_chevron_up"} size={24} color="primary" className="dropdown-icon" />
            </Button>
        </div>
    );

    const toggleBasicInformation = () => {
        setIsBasicInformationHidden(!isBasicInformationHidden);
    }

    const toggleData = () => {
        setIsDataHidden(!isDataHidden);
    }

    const toggleContact = () => {
        setIsContactHidden(!isContactHidden);
    }

    const toggleOther = () => {
        setIsOtherHidden(!isOtherHidden);
    }

    const toggleGovernanceMetadata = () => {
        setIsGovernanceMetadataHidden(!isGovernanceMetadataHidden);
    }

    return (
        <div>
            <div id='basic-information-section'>
                <div className={style.cardHeader}>
                    <div className='basic-information'>
                        <h3>Basic Information</h3>
                    </div>
                    <div className="dropdown-icon-container">
                        <HideExpandButton isHidden={isBasicInformationHidden} toggle={toggleBasicInformation} />
                    </div>
                </div>
                <div className="card-body">
                    <div className={`${style.contentBody} ${isBasicInformationHidden ? style.hidden : style.show}`}>
                        <div className={style.row}>
                            <div className={style.colLg3}>
                                <Field label="Business Unit" value={api.linesOfBusiness} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="Product" value={api.productNameTechnology} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="Area" value={api.area} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="Sub Area 1" value={api.subArea1} />
                            </div>
                        </div>
                        <div className={style.row}>
                            <div className="col-lg-6">
                                <Field label="Description" value={api.apiDescription} />
                            </div>
                            <div className="col-lg-3">
                                <Field label="Date Added" value={formatDateForDisplay(api.dateAdded)} />
                            </div>
                            <div className="col-lg-3">
                                <Field label="Launch Date" value={formatDateForDisplay(api.apiLaunchDate)} />
                            </div>
                            <div className="col-lg-3">
                                <Field label="Sub Area 2" value={api.subArea2} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div id='data-section'>
                <div className={style.cardHeader}>
                    <div className='data'>
                        <h3>Data</h3>
                    </div>
                    <div className="dropdown-icon-container">
                        <HideExpandButton isHidden={isDataHidden} toggle={toggleData} />
                    </div>
                </div>
                <div className="card-body">
                    <div className={`${style.contentBody} ${isDataHidden ? style.hidden : style.show}`}>
                        <div className={style.row}>                            
                            <div className={style.colLg3}>
                                <Field label="Data Type" value={api.dataType} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="Data Sensitivity" value={api.dataSensitivity} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="API Structure" value={api.apiStructure} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="Developer Program" value={api.developerProgram ? "Yes" : "No"} />
                            </div>
                        </div>
                        <div className={style.row}>
                            <div className={style.colLg3}>
                                <Field label="Technical Control Point" value={api.technicalControlPoint} />
                            </div>
                            <div className={style.colLg3}>
                                <Field label="Data Owner" value={api.dataOwner} />
                            </div>
                            <div className={style.colLg3}>
                                <div className="mb-3">
                                    <label className={style.formLabel}>Attachments</label>
                                </div>
                                {api.attachments && api.attachments.filter((a: any) => a && typeof a === 'object' && a.url).length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        {api.attachments
                                            .filter((a: any) => a && typeof a === 'object' && a.url)
                                            .map((attachment: ApiAttachment, index: number) => {
                                            return (
                                                <button
                                                    key={`${attachment.fileName}-${index}`}
                                                    type="button"
                                                    className="d-flex align-items-center btn btn-outline-secondary btn-sm"
                                                    style={{ padding: '0.2rem', border: '1px solid #ccc', borderRadius: '4px' }}
                                                    onClick={() => downloadFile(attachment.url, attachment.fileName)}
                                                >
                                                    <span className="me-2">{attachment.fileName}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className={style.formControlPlaintext}>
                                        <span className="text-muted">No attachments</span>
                                    </div>
                                )}
                            </div>
                            <div className={style.colLg3}>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div id='contact-section'>
                <div className={style.cardHeader}>
                    <div className='contact'>
                        <h3>Contact</h3>
                    </div>
                    <div className="dropdown-icon-container">
                        <HideExpandButton isHidden={isContactHidden} toggle={toggleContact} />
                    </div>
                </div>
                <div className="card-body">
                    <div className={`${style.contentBody} ${isContactHidden ? style.hidden : style.show}`}>
                        <div className={style.row}> 
                            <div className="col-lg-3">
                                <Field label="Product Point of Contact" value={api.productContactEmail} />
                            </div>
                            <div className="col-lg-3">
                                <Field label="Technical Point of Contact" value={api.technicalContactEmail} />
                            </div>
                            {api.developerProgram && (
                                <div className="col-lg-3">
                                    <Field label="Licensing / Program Manager Contact" value={api.licensingProgramManagerContactEmail} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div id='other-section'>
                <div className={style.cardHeader}>
                    <div className='other'>
                        <h3>Other</h3>
                    </div>
                    <div className="dropdown-icon-container">
                        <HideExpandButton isHidden={isOtherHidden} toggle={toggleOther} />
                    </div>
                </div>
                <div className="card-body">
                    <div className={`${style.contentBody} ${isOtherHidden ? style.hidden : style.show}`}>
                        <div className={style.row}>
                            <div className="col-lg-3 col-md-6">
                                <Field label="Dependencies" value={api.dependencies} />
                            </div>
                            <div className="col-lg-3 col-md-6">
                                <Field label="Internal API Repository" value={api.internalApiRepository} isUrl={true} />
                            </div>
                            {api.developerProgram && (
                                <div className="col-lg-3 col-md-6">
                                    <Field label="External API Repository" value={api.externalApiRepository} isUrl={true} />
                                </div>
                            )}
                        </div>
                        <div className={style.row}>
                            <div className="col-lg-3">
                                <Field label="Documentation Link URL" value={api.documentationLink} isUrl={true} />
                            </div>
                            <div className="col-lg-3">
                                {(() => {
                                    if (!api.apiSpecification) {
                                        return <Field label="API Specification file" value={null} />;
                                    }
                                    if (typeof api.apiSpecification === 'string') {
                                        return <Field label="API Specification file" value={api.apiSpecification} isUrl={true} />;
                                    }
                                    if (api.apiSpecification.url) {
                                        const specFileName = api.apiSpecification.fileName || 'specification';
                                        return (
                                            <div className={`mb-3 ${style.field}`}>
                                                <label className={style.formLabel}>API Specification file</label>
                                                <div className={style.formControlPlaintext}>
                                                    <a
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            downloadFile(api.apiSpecification && typeof api.apiSpecification === 'object' ? api.apiSpecification.url : '', specFileName);
                                                        }}
                                                    >
                                                        {specFileName}
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return <Field label="API Specification file" value={null} />;
                                })()}
                            </div>
                            
                        </div>
                        {api.developerProgram && (
                            <div className={style.row}>
                                <div className="col-lg-3 col-md-6">
                                    <Field label="Intellectual Property Approval System (IPAS) Number" value={api.intellectualPropertyApprovalSystemNumber} />
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <Field label="API Data Privacy Assessment" value={api.apiDataPrivacyAssessment} />
                                </div>
                                <div className="col-lg-3 col-md-6">
                                    <Field label="License Type" value={api.licenseType} />
                                </div>
                                <div className="col-lg-3">
                                    <Field label="Export Control Approval/Limitations" value={api.exportControlApprovalLimitations} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {api.apiCenterMetadata && (
                <div id='governance-metadata-section'>
                    <div className={style.cardHeader}>
                        <div className='governance-metadata'>
                            <h3>Governance Metadata</h3>
                        </div>
                        <div className="dropdown-icon-container">
                            <HideExpandButton isHidden={isGovernanceMetadataHidden} toggle={toggleGovernanceMetadata} />
                        </div>
                    </div>
                    <div className="card-body">
                        <div className={`${style.contentBody} ${isGovernanceMetadataHidden ? style.hidden : style.show}`}>
                            <div className={style.row}>
                                <div className={style.colLg3}>
                                    <Field label="API Center ID" value={api.apiCenterMetadata.apiCenterId} />
                                </div>
                                
                                <div className={style.colLg3}>
                                    <Field
                                        label="Health Score"
                                        value={
                                            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                                {api.apiCenterMetadata.healthScore ? (
                                                    <HealthScoreBadge
                                                        score={api.apiCenterMetadata.healthScore.score}
                                                        breakdown={api.apiCenterMetadata.healthScore.breakdown}
                                                        lastCalculated={api.apiCenterMetadata.healthScore.lastCalculated}
                                                    />
                                                ) : (
                                                    <span className="text-muted">Not calculated</span>
                                                )}
                                                
                                                {/* Button is only shown if the API has an API Specification object */}
                                                {hasApiSpec && (
                                                    <Button 
                                                        onClick={() => navigate(`/spec-analysis/${api.id}`)}
                                                        style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                                                    >
                                                        See details
                                                    </Button>
                                                )}
                                            </div>
                                        }
                                    />
                                </div>
                                
                                {api.apiCenterMetadata.lifecycleStage?.trim() && (
                                    <div className={style.colLg3}>
                                        <Field
                                            label="Lifecycle Stage"
                                            value={<LifecycleBadge stage={api.apiCenterMetadata.lifecycleStage} />}
                                        />
                                    </div>
                                )}
                                {api.apiCenterMetadata.apimProducts &&
                                 api.apiCenterMetadata.apimProducts.length > 0 && (
                                    <div className={style.colLg12}>
                                        <Field
                                            label="APIM Products"
                                            value={
                                                <div className="d-flex flex-wrap">
                                                    {api.apiCenterMetadata.apimProducts.map((product, idx) => (
                                                        <ApimProductBadge
                                                            key={`${product.id}-${idx}`}
                                                            productId={product.id}
                                                        />
                                                    ))}
                                                </div>
                                            }
                                        />
                                    </div>
                                )}
                                {api.developerProgram &&
                                 api.apiCenterMetadata.apimProducts &&
                                 api.apiCenterMetadata.apimProducts.length > 0 && (
                                    <div className={style.colLg12}>
                                        <Field
                                            label="External API Endpoints (APIM)"
                                            value={
                                                isLoadingGatewayUrls && !gatewayUrls ? (
                                                    <span className="text-muted">Loading…</span>
                                                ) : gatewayUrls && GATEWAY_ENVIRONMENTS.some((env) => gatewayUrls[env]) ? (
                                                    <div className="d-flex flex-column gap-1">
                                                        {GATEWAY_ENVIRONMENTS.filter((env) => gatewayUrls[env]).map((env) => (
                                                            <div key={env}>
                                                                <strong className="text-uppercase me-2">{env}:</strong>
                                                                <a href={gatewayUrls[env]} target="_blank" rel="noopener noreferrer">{gatewayUrls[env]}</a>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">Not yet onboarded to APIM in any environment</span>
                                                )
                                            }
                                        />
                                        {gatewayUrlsError && (
                                            <div className="text-danger small mt-1">{gatewayUrlsError}</div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApiDetails;